// The Details column for the selected card: Summary, the two trade orders, Next Steps.
//
// Selector is `ets-maven-details` (ruling L20). Lane S6 owns the chassis and places this tag.
//
// The two orders stay TWO transactions and are never merged (D5 / guarantee G11). Hedges is ONE
// control, not separate Calls and Puts, because the dialog contains the regroup and that may build
// iron structures across both sides (D4).
//
// Maven maps NOTHING between a card and a pop-up: both arrays were built on the backend in the
// shapes the pop-ups consume, and they are handed over exactly as delivered. The hand-off itself
// lives in maven-order-dispatch.ts, which is where the reasons are written down.
//
// The right column is about half the screen. Plain text buttons leave it looking empty, so the two
// live orders are large targets. The icon treatment was never approved — the design of record shows
// plain buttons, and the drawing wins over an earlier sketch (ruling L79).
//
// The design of record: https://claude.ai/code/artifact/769bae85-574d-4ec4-b72e-33d3e9147479, plus
// rulings L79-L84. Cited by URL and ruling rather than by revision number, which would rot silently
// while still reading as precision. It is authoritative for the CONTENT of this column; the frame
// and the column proportions are S6's.

import {
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnDestroy,
    ViewChild
} from '@angular/core';
import {Subscription} from 'rxjs';
import {ApgPortfolio} from '../../adjustment-pricing-grid/model/ApgPortfolio';
import {HedgePosition} from '../../hedging-grid/data-model/hedge-position';
import {HedgePositionsService} from '../../hedging-grid/positions-section/hedge-positions/hedge-positions.service';
import {MessageBusService} from '../../message-bus.service';
import type {AdjustmentSolutionPopupComponent} from '../../adjustment-pricing-grid/solution-popup/adjustment-solution-popup.component';
import type {HgTransactionPopupComponent} from '../../hedging-grid/transaction-popup/hg-transaction-popup.component';
import {
    HG_SAVE_AFTER_STATE_TOPIC,
    MavenAppliedMarksService,
    mavenCardKey,
    MavenSaveSlot
} from '../results/maven-applied-marks.service';
import {MavenCard, MavenResultReady} from '../results/maven-delivery.contract';
import {mavenAdjustmentTypesText, mavenCardDateText, mavenMoneyText} from '../results/maven-card-format';
import {openMavenAdjustmentOrder, openMavenHedgesOrder} from './maven-order-dispatch';

@Component({
    selector: 'ets-maven-details',
    templateUrl: './maven-details.component.html',
    styleUrls: ['./maven-details.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MavenDetailsComponent implements AfterViewInit, OnDestroy {

    @Input() card: MavenCard | null = null;

    /** The run-level facts both pop-ups need: the default quantity and the strategy type. */
    @Input() delivery: MavenResultReady | null = null;

    @Input() portfolio: ApgPortfolio | null = null;

    @ViewChild('adjustmentPopup') adjustmentPopup: AdjustmentSolutionPopupComponent;

    @ViewChild('hedgesPopup') hedgesPopup: HgTransactionPopupComponent;

    // ⚠ THIS COLUMN SHOWS NO MARK. The two order buttons keep one label and one look whatever has
    // been sent (owner, vrequest 2026-09-13: "just leave that button as is"). The A and H marks on
    // the card in the Results column are the only surface that says what was copied from; a second
    // reader here was the "Adjustment ◆ sent" caption the owner asked to be removed.
    //
    // It does READ the marks store for ONE thing (owner, vrequest + rulings 2026-09-15,
    // CR-20260915-003): whether ANOTHER card is applied. While one card has any half of A or H lit,
    // every other card is read-only: its two buttons still open the pop-ups so the trader can SEE
    // the orders, but each pop-up's Copy / Regroup bar is replaced by the lock hint (the pop-ups'
    // `lockHint` input). No text in this section itself (owner, fourth look). No warning, no dialog.

    constructor(
        private readonly _marks: MavenAppliedMarksService,
        private readonly _messageBus: MessageBusService,
        private readonly _hedgePositions: HedgePositionsService,
        private readonly _cdr: ChangeDetectorRef
    ) {
        // ⚠ THE HEDGE POP-UP PERSISTS NOTHING. On Copy it publishes 'Hg.SaveAfterState' and stops;
        // the CALLER that opened it writes the after-state — the Hedging Grid's positions section
        // does so when it is materialized on that portfolio (hg-hedge-positions.component.ts:135),
        // Package Comparison for its own package id (package-comparison.component.ts:2032). Maven
        // never did, and it went unnoticed while the H mark lit on the echo alone. Now a mark is a
        // view of the SAVED state, so with no Hedging Grid open the H halves stayed dark (owner smoke
        // 2026-09-15). Maven saves its own copy, matched by the card key it handed the pop-up, the
        // same way Package Comparison does; when a Hedging Grid IS open on the portfolio both write
        // the same value to the same key, which is harmless.
        this._saveSubscription = this._messageBus
            .of<{ positions?: HedgePosition[]; slot?: MavenSaveSlot; packageId?: string; portfolio?: ApgPortfolio }>(HG_SAVE_AFTER_STATE_TOPIC)
            .subscribe(msg => this.onHedgesSaved(msg.payload));
    }

    private _marksSubscription: Subscription | null = null;

    private _saveSubscription: Subscription | null = null;

    // Same shape as the card: an OnPush leaf under a detached workspace repaints itself on a store
    // change (a pop-up's save, a clear in the APG or the Hedging Grid), because nothing above it
    // runs a tick for a message-bus event. Subscribed after the view exists, never in the
    // constructor — `changed` is a BehaviorSubject and replays at once.
    ngAfterViewInit(): void {
        this._marksSubscription = this._marks.changed.subscribe(() => this._cdr.detectChanges());
    }

    ngOnDestroy(): void {
        if (this._marksSubscription !== null) {
            this._marksSubscription.unsubscribe();
            this._marksSubscription = null;
        }

        if (this._saveSubscription !== null) {
            this._saveSubscription.unsubscribe();
            this._saveSubscription = null;
        }
    }

    /**
     * The hedge pop-up echoed a save. Only a save carrying one of THIS run's card keys is Maven's to
     * persist — a save from the Hedging Grid's own pop-up carries no id, Package Comparison's carries
     * its own — and it is written against the run's portfolio, which is what the pop-up was opened
     * on. The store then publishes 'Hg.AfterStateUpdated' and the marks re-derive from it.
     */
    private onHedgesSaved(payload: { positions?: HedgePosition[]; slot?: MavenSaveSlot; packageId?: string; portfolio?: ApgPortfolio } | null): void {

        if (!payload || !payload.packageId || !this.delivery || !this.portfolio) {
            return;
        }

        const isOurs = (this.delivery.cards || []).some(c => c && mavenCardKey(c) === payload.packageId);

        if (!isOurs || (payload.slot !== 'first' && payload.slot !== 'second')) {
            return;
        }

        this._hedgePositions.saveAfterState(payload.positions || [], payload.slot, this.portfolio);
    }

    get hasCard(): boolean {
        return !!this.card;
    }

    /**
     * True when a DIFFERENT card is applied: some half of its A or H is lit, so this card's orders
     * are read-only until that saved state is cleared where it lives.
     *
     * READ-ONLY, NOT DISABLED (owner, 2026-09-15, second look): disabling the two buttons kept the
     * trader from seeing what a locked card's orders offered. So the buttons stay live, the pop-ups
     * open, and it is the pop-up's Copy / Regroup bar that is replaced by the hint (`lockHint`
     * input on both pop-ups). Nothing is shown in this section itself (owner, fourth look).
     */
    get lockedByAnotherCard(): boolean {

        const applied = this._marks.appliedCardKey;

        return applied !== null && !!this.card && applied !== mavenCardKey(this.card);
    }

    /** The one line that says why copying is off and where to release it. Null when it is not. */
    get lockHint(): string | null {

        if (!this.lockedByAnotherCard) {
            return null;
        }

        const holder = this.appliedCardDateText;

        return (holder ? `Orders are already copied from the ${holder} card. ` : 'Orders are already copied from another card. ')
            + 'Clear the saved adjustments and hedges to release it.';
    }

    /** The card that holds the lock, by its date, for the tooltip. Null when none does. */
    get appliedCardDateText(): string | null {

        const applied = this._marks.appliedCardKey;

        if (applied === null || !this.delivery) {
            return null;
        }

        const card = (this.delivery.cards || []).filter(c => c && mavenCardKey(c) === applied)[0];

        return card ? mavenCardDateText(card.date, card.dte) : null;
    }

    get dateText(): string {
        return this.card ? mavenCardDateText(this.card.date, this.card.dte) : '';
    }

    /** The best price — the ledger figure, per unit, the same number the card shows large. */
    get bestPriceText(): string {
        return mavenMoneyText(this.card?.prices?.total);
    }

    // Cost per day is no longer here: it moved to the card, under its price (owner, vrequest
    // 2026-09-13, replacing ruling D2). The card component owns the figure and its text.

    /**
     * The adjustment types this package represents — `2B(C) + 2B(P)`, `3(C) + 3(P)` — as a Pros
     * row (owner, 2026-09-21). The same formatter as the card's Adjustment line, so the two never
     * disagree. Null hides the row.
     */
    get adjustmentTypesText(): string | null {
        return mavenAdjustmentTypesText(this.card?.adjustment?.adjustmentTypes);
    }

    /**
     * The structure the hedge engine ACTUALLY delivered per side, named in full — "Condor - Low",
     * "Unbalanced Condor - Aggressive" (CR-20260917-001).
     *
     * Not the coverage the trader asked for: the engine reports no coverage kind on a single-family
     * run, so naming the request here would state something the run never established.
     *
     * The whole string is composed on the backend and rendered verbatim. Nothing here parses it,
     * splits it or re-derives the "Unbalanced" word from the legs — the ORION declares the shape.
     */
    get callsDelivered(): string | null {
        return this.card?.coverageDelivered?.calls || null;
    }

    get putsDelivered(): string | null {
        return this.card?.coverageDelivered?.puts || null;
    }

    /**
     * The report on this package's OWN date, named by tier (CR-20260921-001, owner ruling
     * 2026-09-21 — replaces the neighbour window of contract M1 item 11 / ruling L14).
     *
     * Presentation only — it never reorders, filters or annotates the ranking. A day with no
     * Tier 1 / Tier 2 report shows nothing: no "clear" line, no neighbour. A day the calendar
     * cannot vouch for carries no entry and is never presented as clear. Same rule as the card
     * chip, so the two never disagree.
     */
    get nearbyReports(): { label: string; text: string }[] {

        const own = this.card?.date;

        return (this.card?.nearbyReports || [])
            .filter(r => r && r.date === own && (r.tier === 'tier1' || r.tier === 'tier2'))
            .map(r => ({
                label: r.tier === 'tier1' ? 'TIER 1' : 'TIER 2',
                text: (r.events || []).join(', ')
            }));
    }

    /**
     * Whether the run carries what both pop-ups need to open at all.
     *
     * `strategyType` is delivered and never inferred (ruling L39). It now arrives on every succeeded
     * run — the frontend used to drop it, which is why these two controls were dead.
     *
     * THE GUARD STAYS, and is not dead reasoning: both ways of getting this flag wrong are SILENT —
     * the hedge pop-up filters legs out of the transaction inside itself, and the adjustment writes
     * its after-state into the wrong slot. So if it is ever absent the controls are disabled rather
     * than offered. A button that looks live and quietly does nothing is the same class of defect as
     * a figure that looks live and never moves, and defaulting the flag would be worse than both.
     */
    get canOrder(): boolean {
        const type = this.delivery ? this.delivery.strategyType : null;

        return !!this.portfolio && (type === 'single' || type === 'double');
    }

    /** Said plainly, beside the controls, rather than left as a dead click. */
    get cannotOrderReason(): string | null {
        if (this.canOrder) {
            return null;
        }

        if (!this.portfolio) {
            return 'Select a portfolio to send orders.';
        }

        return 'Orders are unavailable for this run.';
    }

    /**
     * Open the adjustment order. The mark lights only if the pop-up actually opened — a mark on an
     * order the trader never saw would claim something false.
     */
    onAdjustment(): void {

        if (!this.card || !this.delivery || !this.canOrder) {
            return;
        }

        openMavenAdjustmentOrder(
            this.adjustmentPopup,
            {
                portfolio: this.portfolio,
                defaultQty: this.delivery.defaultQty,
                strategyType: this.delivery.strategyType
            },
            this.card,
            mavenCardKey(this.card)
        );

        // ⚠ NO MARK HERE. A mark means "I hit Copy", not "I opened the dialog" (owner ruling,
        // 2026-09-11) — and it is a VIEW of the saved state, so the save is what lights it and a
        // clear is what puts it out. Lighting on open claimed something the trader had not done.
        //
        // The adjustment half of that route does not exist yet: its pop-up takes no identity and
        // publishes an empty payload, so nothing tells Maven which card a save belongs to. Raised
        // with the leader rather than guessed. Until it is settled the A mark stays unlit, which is
        // silent but not false.
    }

    /** Open the hedge order — close of the old and opening of the new, in one dialog. */
    onHedges(): void {

        if (!this.card || !this.delivery || !this.canOrder) {
            return;
        }

        // The card's own key travels as the pop-up's package id, so its save comes back identified
        // and the mark is set by the SAVE rather than by this click.
        openMavenHedgesOrder(
            this.hedgesPopup,
            {
                portfolio: this.portfolio,
                defaultQty: this.delivery.defaultQty,
                strategyType: this.delivery.strategyType
            },
            this.card,
            mavenCardKey(this.card)
        );
    }

    // ⚠ THE LOCAL RESET CONTROLS ARE GONE, and their absence is the point.
    //
    // A mark is now a VIEW of the saved state (owner ruling, 2026-09-11). A control that put the
    // mark out WITHOUT clearing the state it reflects would say "nothing is saved" while a saved
    // after-state still sat in the APG or the Hedging Grid — the same shape of lie as a figure that
    // looks live and never moves.
    //
    // Clearing is done where the state lives: clear it in the APG or the Hedging Grid and the mark
    // goes out here on its own. That is what D8 asks for — what you get into you can get out of —
    // and it is now one mechanism instead of two that can disagree.
}

